import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Box, Typography, IconButton, CircularProgress } from "@mui/material";
import { GoogleMap, Marker } from "@react-google-maps/api";
import CustomButton from "../../../../components/CustomButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import LocationPermissionModal from "../../../../components/PermissionModal";
import {
  getPositionWithFallback,
  useLocationPermissionSync,
} from "../../../../utils/getPositionWithFallback";
const defaultCenter = { lat: 33.6844, lng: 73.0479 };
const defaultZoom = 15;
const defaultMarkerUrl = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png";

const MapModal = ({ open, onClose, onContinue }) => {
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState("Fetching address...");
  const addressRef = useRef(null);
  const mapRef = useRef(null);
  const { t,i18n } = useTranslation();
const [locationError, setLocationError] = useState("");
const [locationFailed, setLocationFailed] = useState(false);
  const [locationUnavailable, setLocationUnavailable] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  // 🔄 Reset position state when modal opens - always fetch fresh location
  React.useEffect(() => {
    if (open) {
     setPosition(null);
    setLocationError("");
    setLocationFailed(false);   // 👈 reset
    setLocationUnavailable(false);
    setShowPermissionModal(false);
    setAddress("Fetching address...");
      console.log("🗺️ Map modal opened - fetching fresh location");
    }
  }, [open]);

  const rawEncryptedToken = localStorage.getItem("token");
const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

const validateLocation = async (lat, lng) => {
  try {
    const res = await fetch(`${BASE_URL}/attendance/validate-location`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        gps_lat: lat,
        gps_lng: lng,
      }),
    });

    const data = await res.json();
    console.log("📍 Location validation response:", data);

    const backendMessage =
      data?.[`message_${i18n.language}`] || data?.message;

if (!res.ok || data?.error || data?.data?.valid === false) {
  setLocationError(
    i18n.language === "es"
      ? "Su ubicación no coincide con la ubicación registrada; por lo tanto, este registro será considerado inválido."
      : "Your location does not match the registered work location; therefore, this punch will be considered an invalid punch."
  );
  return false;
}

    setLocationError("");
    toast.success(backendMessage || "Location valid ✅");
    return true;

  } 
  catch (err) {
  setLocationError(
    i18n.language === "es"
      ? "Su ubicación no coincide con la ubicación registrada; por lo tanto, este registro será considerado inválido."
      : "Your location does not match the registered work location; therefore, this punch will be considered an invalid punch."
  );
  return false;
}
};
  // reverse geocode
  const fetchAddress = async ({ lat, lng }) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { "User-Agent": "yourapp/1.0 (contact@example.com)" } }
      );
      const data = await res.json();
      const formatted = data?.display_name || "Address not found";
      setAddress(formatted);
      if (addressRef.current) addressRef.current.value = formatted;
    } catch {
      setAddress("Address not found");
      if (addressRef.current) addressRef.current.value = "Address not found";
    }
  };

  // fetch current location on open
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) return () => {};
    return getPositionWithFallback(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const coords = { lat: latitude, lng: longitude, accuracy_m: accuracy || 5 };
        console.log("📍 Current user location:", coords);
        setPosition(coords);
        fetchAddress(coords);
      },
      (err) => {
        console.error("❌ Failed to get current location:", err);
        // Never invent a position for a punch: no fix means no position.
        setPosition(null);
        setLocationUnavailable(true);
        if (err?.code === err?.PERMISSION_DENIED) {
          setShowPermissionModal(true);
        } else {
          toast.error(
            t("locationFetchFailed") ||
              "Unable to fetch your location. Please make sure Location Services are on and try again."
          );
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    return requestLocation();
  }, [open, requestLocation]);

  // Re-fetch when the user flips Location to Allow while the modal is open
  useLocationPermissionSync({
    enabled: open,
    onGranted: () => {
      setShowPermissionModal(false);
      setLocationUnavailable(false);
      setPosition(null);
      requestLocation();
    },
    onDenied: () => {
      setPosition(null);
      setLocationUnavailable(true);
      setShowPermissionModal(true);
    },
  });

  const fitMapToPosition = useCallback((map) => {
    if (!map || !window.google) return;

    if (!position) {
      map.setCenter(defaultCenter);
      map.setZoom(defaultZoom);
      return;
    }

    map.setCenter({ lat: position.lat, lng: position.lng });
    map.setZoom(defaultZoom);
  }, [position]);

  const getMarkerIcon = useCallback(() => {
    if (!window.google) {
      return { url: defaultMarkerUrl };
    }

    return {
      url: defaultMarkerUrl,
      scaledSize: new window.google.maps.Size(25, 41),
      anchor: new window.google.maps.Point(12, 41),
    };
  }, []);

  useEffect(() => {
    fitMapToPosition(mapRef.current);
  }, [fitMapToPosition]);

  return (
    <Modal open={open} onClose={onClose}>

      <Box
  sx={{
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "white",
    p: 2,
    borderRadius: 2,
    maxWidth: 700,
    width: "100%",
    maxHeight: "95vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  }}
>

        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 5, right: 30, color: "#333" }}
        >
          <CloseRoundedIcon />
        </IconButton>

        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
          {/* Title */}
          <Typography
            sx={{
              color: "#2C384C",
              fontSize: "26px",
              fontWeight: 600,
              textAlign: "center",
              fontFamily: "Poppins, sans-serif",
              mt: 4,
            }}
          >
            {t("mapModal.title")}
          </Typography>
          <p
            style={{
              fontSize: "14px",
              color: "#8B8D97",
              textAlign: "center",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {t("mapModal.subtitle")}
          </p>

          {/* Map */}
          {position ? (
            <GoogleMap
              center={{ lat: position.lat, lng: position.lng }}
              zoom={defaultZoom}
              mapContainerStyle={{ height: 350, width: "100%", marginTop: "10px" }}
              options={{ streetViewControl: false, mapTypeControl: false }}
              onLoad={(map) => {
                mapRef.current = map;
                fitMapToPosition(map);
              }}
            >
              <Marker
                position={{ lat: position.lat, lng: position.lng }}
                icon={getMarkerIcon()}
              />
            </GoogleMap>
          ) : (
            <Box
              sx={{
                height: 350,
                width: "100%",
                mt: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#F5F5F5",
                borderRadius: 1,
              }}
            >
              {locationUnavailable ? (
                <Box sx={{ textAlign: "center", px: 2 }}>
                  <Typography sx={{ color: "red", fontSize: "14px", fontFamily: "Poppins, sans-serif", mb: 1.5 }}>
                    {t("locationFetchFailed") ||
                      "Unable to fetch your location. Please make sure Location Services are on and try again."}
                  </Typography>
                  <CustomButton
                    label={t("retry") || "Retry"}
                    size="small"
                    onClick={() => {
                      setLocationUnavailable(false);
                      requestLocation();
                    }}
                  />
                </Box>
              ) : (
                <CircularProgress size={32} sx={{ color: "#006EC2" }} />
              )}
            </Box>
          )}

        
        </Box>

{locationError && (
  <Typography
    sx={{
      color: "red",
      fontSize: "14px",
      mt: 2,
      textAlign: "center",
      fontFamily: "Poppins, sans-serif",
    }}
  >
    {locationError}
  </Typography>
)}

        {/* Fixed footer */}
        <Box sx={{ mt: 2 }}>
          <CustomButton
            label={t("mapModal.continue")}
            size="small"
            fullWidth
//             onClick={async () => {
//   if (!position) {
//     toast.error("❌ Location not available");
//     return;
//   }

//   const isValid = await validateLocation(position.lat, position.lng);

//   const payload = {
//     geo: {
//       lat: position.lat,
//       lng: position.lng,
//       accuracy_m: position.accuracy_m || 5,
//     },
//     address,
//     local_timestamp: new Date().toISOString(),
//   };

//   if (!isValid) {
//     console.log("❌ Location invalid — waiting 1.5 sec");

//     setTimeout(() => {
//       onContinue(payload);
//       onClose();
//     }, 1500);

//     return;
//   }

//   // ✅ If valid → continue immediately
//   onContinue(payload);
//   onClose();
// }}

onClick={async () => {
  if (!position) {
    toast.error("❌ Location not available");
    return;
  }

  const payload = {
    geo: {
      lat: position.lat,
      lng: position.lng,
      accuracy_m: position.accuracy_m || 5,
    },
    address,
    local_timestamp: new Date().toISOString(),
  };

  // If already failed once → allow navigation
  if (locationFailed) {
    onContinue(payload);
    onClose();
    return;
  }

  const isValid = await validateLocation(position.lat, position.lng);

  if (!isValid) {
    setLocationFailed(true);   // 👈 allow next click
    return;
  }

  onContinue(payload);
  onClose();
}}
          
            disabled={!position}
          />
        </Box>

        <LocationPermissionModal
          open={showPermissionModal}
          onCancel={() => setShowPermissionModal(false)}
        />
      </Box>
    </Modal>
  );
};

export default MapModal;






